import { cn } from "../../../utils/cn.js";
import { mobileSafeInputTextSizeClasses } from "../shared/form-field-variants.js";
import { useHorizontalInputScrollFade } from "../shared/use-horizontal-input-scroll-fade.js";
import { cva } from "class-variance-authority";
import * as React from "react";
import { jsx, jsxs } from "react/jsx-runtime";
import { Field } from "@base-ui/react/field";
import { Input } from "@base-ui/react/input";
import { PatternFormat } from "react-number-format";
//#region src/components/TextInput/TextInput.tsx
const SizeContext = React.createContext("base");
const VariantContext = React.createContext("default");
const labelVariants = cva([
	"tracking-sm m-0",
	"text-text-primary",
	"group-data-[disabled]:text-text-disabled"
], {
	defaultVariants: { size: "base" },
	variants: { size: {
		small: "text-body-small leading-body-small font-heading-medium",
		base: "text-body-small leading-body-small font-heading-medium",
		large: "text-body-small leading-body-small font-body-regular"
	} }
});
const controlVariants = cva(["flex items-center gap-xs w-full box-border", "transition-colors duration-150"], {
	defaultVariants: {
		size: "base",
		variant: "default"
	},
	variants: {
		size: {
			small: "h-[28px] px-xs py-0 [&_svg]:size-[12px]",
			base: "h-[32px] px-xs py-0 [&_svg]:size-[12px]",
			large: "h-[36px] px-xs py-0 [&_svg]:size-[14px]"
		},
		variant: {
			default: [
				"rounded-xs border-(length:--border-width-default) border-solid",
				"bg-background-input border-border-input",
				"group-not-data-[disabled]:not-focus-within:hover:bg-background-input-hover",
				"focus-within:bg-background-input-pressed focus-within:border-border-focused",
				"group-data-[invalid]:border-border-danger",
				"group-data-[tone=warning]:border-border-warning",
				"group-data-[disabled]:bg-background-disabled group-data-[disabled]:border-border-disabled"
			],
			flat: "border-0 bg-transparent"
		}
	}
});
const inputBaseClasses = [
	"w-0 flex-1 min-w-0 bg-transparent border-none outline-none p-0 m-0",
	"font-body font-heading-medium text-text-primary",
	"placeholder:text-text-tertiary placeholder:font-heading-medium",
	"disabled:text-text-disabled disabled:placeholder:text-text-disabled disabled:cursor-not-allowed"
];
const inputSizeClasses = {
	small: mobileSafeInputTextSizeClasses.base,
	base: mobileSafeInputTextSizeClasses.base,
	large: mobileSafeInputTextSizeClasses.large
};
const helperTextVariants = cva([
	"tracking-sm m-0",
	"text-text-secondary",
	"group-data-[disabled]:text-text-disabled"
], {
	defaultVariants: { size: "base" },
	variants: { size: {
		small: "text-body-xsmall leading-body-xsmall font-heading-medium",
		base: "text-body-xsmall leading-body-xsmall font-heading-medium",
		large: "text-body-small leading-body-small font-body-regular"
	} }
});
const errorTextVariants = cva([
	"tracking-sm m-0",
	"text-text-danger",
	"group-data-[tone=warning]:text-text-warning",
	"group-data-[disabled]:text-text-disabled"
], {
	defaultVariants: { size: "base" },
	variants: { size: {
		small: "text-body-xsmall leading-body-xsmall font-heading-medium",
		base: "text-body-xsmall leading-body-xsmall font-heading-medium",
		large: "text-body-small leading-body-small font-body-regular"
	} }
});
/**
* Curri-styled field root built on the Base UI Field primitive.
*
* Provides size and variant context to child sub-components and applies the
* `group` class so descendant elements react to field state via
* `group-data-[...]` selectors.
*
* @param props - Field configuration, size variant, and visual variant
* @returns The rendered field root
*/
function TextInputRoot({ size = "base", variant = "default", className, ...props }) {
	return /* @__PURE__ */ jsx(SizeContext.Provider, {
		value: size,
		children: /* @__PURE__ */ jsx(VariantContext.Provider, {
			value: variant,
			children: /* @__PURE__ */ jsx(Field.Root, {
				className: cn("group flex min-w-0 flex-col gap-xxs font-body", className),
				...props
			})
		})
	});
}
/**
* Curri-styled field label built on the Base UI Field.Label primitive.
*
* Automatically associates with the field control for accessibility.
* Typography scales with the parent `TextInputRoot` size context.
*
* @param props - Label content and optional className overrides
* @returns The rendered label element
*/
const TextInputLabel = React.forwardRef(function TextInputLabel({ className, ...props }, ref) {
	const size = React.useContext(SizeContext);
	return /* @__PURE__ */ jsx(Field.Label, {
		ref,
		className: cn(labelVariants({ size }), className),
		...props
	});
});
/**
* Styled container for the input element and optional icons.
*
* Renders the bordered input field box that reacts to parent `TextInputRoot`
* state via `group-data-[...]` selectors for focus, error, and disabled
* treatments. Uses `focus-within:` for focus detection so it works with
* any child input type (Base UI Input, textarea, formatted input).
*
* Long values (and clipped long placeholders) get horizontal edge fades via
* {@link useHorizontalInputScrollFade}, matching table overflow affordances.
*
* @param props - Container props and optional className overrides
* @returns The rendered input container
*/
const TextInputControl = React.forwardRef(function TextInputControl({ className, ...props }, ref) {
	const size = React.useContext(SizeContext);
	const variant = React.useContext(VariantContext);
	const containerRef = React.useRef(null);
	useHorizontalInputScrollFade(containerRef);
	return /* @__PURE__ */ jsx("div", {
		ref: React.useCallback((node) => {
			containerRef.current = node;
			if (typeof ref === "function") ref(node);
			else if (ref) ref.current = node;
		}, [ref]),
		className: cn(controlVariants({
			size,
			variant
		}), className),
		...props
	});
});
/**
* Curri-styled input element built on the Base UI Input primitive.
*
* Renders a transparent, borderless `<input>` that inherits its visual
* treatment from the parent `TextInputControl` container. Automatically
* integrates with the Base UI Field context for accessibility and state
* management.
*
* @param props - Input configuration forwarded to the Base UI Input primitive
* @returns The rendered input element
*/
const TextInputInput = React.forwardRef(function TextInputInput({ className, ...props }, ref) {
	const size = React.useContext(SizeContext);
	return /* @__PURE__ */ jsx(Input, {
		ref,
		className: cn(inputBaseClasses, inputSizeClasses[size], className),
		...props
	});
});
/**
* Curri-styled helper text built on the Base UI Field.Description primitive.
*
* Renders a `<p>` element automatically linked to the field control via
* `aria-describedby`. Typography scales with the parent size context.
*
* @param props - Description content and optional className overrides
* @returns The rendered helper text element
*/
const TextInputHelperText = React.forwardRef(function TextInputHelperText({ className, ...props }, ref) {
	const size = React.useContext(SizeContext);
	return /* @__PURE__ */ jsx(Field.Description, {
		ref,
		className: cn(helperTextVariants({ size }), className),
		...props
	});
});
/**
* Curri-styled error message built on the Base UI Field.Error primitive.
*
* Uses `match` to always render when present in the tree, giving consumers
* full control over visibility via conditional rendering. Typography scales
* with the parent size context.
*
* @param props - Error content and optional className overrides
* @returns The rendered error message element
*/
const TextInputErrorText = React.forwardRef(function TextInputErrorText({ className, ...props }, ref) {
	const size = React.useContext(SizeContext);
	return /* @__PURE__ */ jsx(Field.Error, {
		ref,
		match: true,
		className: cn(errorTextVariants({ size }), className),
		...props
	});
});
/**
* Preserves literal mask config while requiring `numberFormat` on each entry.
*
* @param masks - Named preset definitions keyed by {@link TextInputMask}
* @returns The same masks object with inferred const types
*/
function defineTextInputMasks(masks) {
	return masks;
}
/**
* Built-in masked entry presets for common Curri form fields.
*
* Use via `TextInput` `mask`. Explicit `type`, `numberFormat`, `inputMode`, etc.
* on the component override preset fields.
*/
const TEXT_INPUT_MASKS = defineTextInputMasks({
	"phone-us": {
		autoComplete: "tel",
		inputMode: "tel",
		numberFormat: "+1 (###) ###-####",
		numberMask: "_",
		type: "tel"
	},
	"ssn-us": {
		inputMode: "numeric",
		numberFormat: "###-##-####",
		numberMask: "_",
		type: "text"
	},
	"zip-us": {
		inputMode: "numeric",
		numberFormat: "#####",
		numberMask: "_",
		type: "text"
	}
});
/** All {@link TextInputMask} keys as a typed array (e.g. Storybook controls). */
const TEXT_INPUT_MASK_KEYS = Object.keys(TEXT_INPUT_MASKS);
const DigitPatternInput = PatternFormat;
/**
* Curri-styled text input built on Base UI Field and Input primitives.
*
* Supports single-line input, multi-line textarea, and digit-masked entry via
* `mask` presets or freeform `numberFormat`. Wraps the full Base UI Field
* structure (label, control, description, error) for automatic accessibility
* wiring and field state management. Hover, focus, error, and disabled
* treatments match the Figma spec.
*
* @param props - Input configuration including label, helper/error text, icons, and size variant
* @returns The rendered text input field
*/
function TextInput({ className, defaultValue, disabled, error = false, warning = false, errorText, helperText, iconLeft, iconRight, inputRef, label, mask, multiline = false, multilineClassName, inputClassName: inputClassNameProp, numberFormat: numberFormatProp, numberMask: numberMaskProp, onChange, rows = 2, size = "base", variant = "default", type: typeProp, value, ...props }) {
	const maskDefaults = mask ? TEXT_INPUT_MASKS[mask] : void 0;
	const type = typeProp ?? maskDefaults?.type ?? "text";
	const numberFormat = numberFormatProp ?? maskDefaults?.numberFormat;
	const numberMask = numberMaskProp ?? maskDefaults?.numberMask ?? "_";
	const { autoComplete: autoCompleteProp, inputMode: inputModeProp, ...inputProps } = props;
	const nativeInputProps = {
		...inputProps,
		...autoCompleteProp ?? maskDefaults?.autoComplete ? { autoComplete: autoCompleteProp ?? maskDefaults?.autoComplete } : {},
		...inputModeProp ?? maskDefaults?.inputMode ? { inputMode: inputModeProp ?? maskDefaults?.inputMode } : {}
	};
	const inputClassName = cn(inputBaseClasses, inputSizeClasses[size], inputClassNameProp);
	const digitFormat = numberFormat ?? (type === "tel" ? TEXT_INPUT_MASKS["phone-us"].numberFormat : void 0);
	return /* @__PURE__ */ jsxs(TextInputRoot, {
		size,
		variant,
		disabled,
		invalid: Boolean(error && !warning) || void 0,
		"data-tone": Boolean(warning && error) ? "warning" : void 0,
		className,
		children: [
			label && /* @__PURE__ */ jsx(TextInputLabel, { children: label }),
			/* @__PURE__ */ jsxs(TextInputControl, {
				className: multiline ? "h-auto min-h-[56px] items-start py-xs" : void 0,
				children: [
					iconLeft,
					multiline ? /* @__PURE__ */ jsx("textarea", {
						ref: inputRef,
						disabled,
						rows,
						className: cn(inputClassName, "min-h-[56px] resize-y", multilineClassName),
						value: value ?? void 0,
						defaultValue: defaultValue ?? void 0,
						onChange,
						...nativeInputProps
					}) : digitFormat ? /* @__PURE__ */ jsx(DigitPatternInput, {
						format: digitFormat,
						mask: numberMask,
						className: inputClassName,
						disabled,
						ref: inputRef,
						value: value ?? void 0,
						defaultValue: defaultValue ?? void 0,
						onChange,
						...nativeInputProps
					}) : /* @__PURE__ */ jsx(Input, {
						ref: inputRef,
						type,
						className: inputClassName,
						value: value ?? void 0,
						defaultValue: defaultValue ?? void 0,
						onChange,
						...nativeInputProps
					}),
					iconRight
				]
			}),
			error && errorText ? /* @__PURE__ */ jsx(TextInputErrorText, { children: errorText }) : helperText ? /* @__PURE__ */ jsx(TextInputHelperText, { children: helperText }) : null
		]
	});
}
//#endregion
export { TEXT_INPUT_MASKS, TEXT_INPUT_MASK_KEYS, TextInput, TextInputControl, TextInputErrorText, TextInputHelperText, TextInputInput, TextInputLabel, TextInputRoot };

//# sourceMappingURL=TextInput.js.map